import { type MailboxTestStatus, residenceSendMailboxTest } from '@repo/sdk/gen'
import { Badge, type BadgeVariant } from '@repo/ui/badge'
import { Button } from '@repo/ui/button'
import { DetailSection } from '@repo/ui/components/detail-section'
import { formatLocaleDate } from '@repo/ui/dates'
import { Skeleton } from '@repo/ui/skeleton'
import { Spinner } from '@repo/ui/spinner'
import { formatError } from '@repo/ui/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MailCheck, Send } from 'lucide-react'
import { toast } from 'sonner'
import { type TKey, useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'
import { residenceMailboxTestQueryOptions } from '../../-query/query-options'

/**
 * "Email di prova": the general admin follows the test of the PEC of the call
 * and may send it for the manager; once the call is published a new one is a
 * check that the mailbox still receives, with no code to type.
 */
function MailboxTestSection({
	residenceId,
	pecAddress,
	published
}: {
	residenceId: string
	pecAddress: string | null
	/** The call is published: a new test only checks the mailbox. */
	published: boolean
}) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const query = useQuery(residenceMailboxTestQueryOptions(residenceId))

	const send = useMutation({
		mutationFn: () =>
			residenceSendMailboxTest({ body: { data: { residenceId } } }),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.residences.mailboxTest(residenceId)
			})
			toast.success(t('admin-residences:mailbox_test.sent'))
		},
		onError: (error) => toast.error(formatError(error))
	})
	const test = query.data?.data ?? null
	let sendLabel: TKey = 'admin-residences:mailbox_test.send'
	if (published) {
		sendLabel = 'admin-residences:mailbox_test.check'
	} else if (test) {
		sendLabel = 'admin-residences:mailbox_test.send_again'
	}

	return (
		<DetailSection
			action={
				<Button
					disabled={!pecAddress || send.isPending}
					onClick={() => send.mutate()}
					size='sm'
					title={
						pecAddress
							? undefined
							: t('admin-residences:mailbox_test.no_address')
					}
					variant='outline'
				>
					{send.isPending ? <Spinner /> : <Send />}
					{t(sendLabel)}
				</Button>
			}
			icon={MailCheck}
			title={t('admin-residences:mailbox_test.title')}
		>
			{query.isLoading && <Skeleton className='h-16' />}
			{query.isError && (
				<p className='text-muted-foreground text-sm'>
					{t('admin-residences:mailbox_test.error')}
				</p>
			)}
			{query.isSuccess && !test && (
				<p className='text-muted-foreground text-sm'>
					{pecAddress
						? t('admin-residences:mailbox_test.none', {
								address: pecAddress
							})
						: t('admin-residences:mailbox_test.no_address')}
				</p>
			)}
			{published && (
				<p className='pb-3 text-muted-foreground text-sm'>
					{t('admin-residences:mailbox_test.published_note')}
				</p>
			)}
			{test && (
				<dl className='grid gap-3 text-sm sm:grid-cols-[12rem_1fr]'>
					<dt className='text-muted-foreground'>
						{t('admin-residences:mailbox_test.delivery')}
					</dt>
					<dd className='flex flex-col gap-1'>
						<span>
							<Badge variant={DELIVERY_TONE[test.delivery]}>
								{t(DELIVERY_LABEL[test.delivery])}
							</Badge>
						</span>
						{test.delivery === 'refused' && (
							<span className='text-destructive'>
								{t(
									'admin-residences:mailbox_test.refused_hint'
								)}
							</span>
						)}
					</dd>
					<dt className='text-muted-foreground'>
						{t('admin-residences:mailbox_test.sent_to')}
					</dt>
					<dd>
						{t('admin-residences:mailbox_test.sent_on', {
							address: test.address,
							date: formatLocaleDate(test.sentAt, 'P HH:mm')
						})}
					</dd>
					{/* A test the mailbox refused has no code to wait for. */}
					{!published && test.delivery !== 'refused' && (
						<>
							<dt className='text-muted-foreground'>
								{t('admin-residences:mailbox_test.code')}
							</dt>
							<dd>
								{test.confirmedAt
									? t(
											'admin-residences:mailbox_test.confirmed',
											{
												name:
													test.confirmedByName ?? '',
												date: formatLocaleDate(
													test.confirmedAt,
													'P HH:mm'
												)
											}
										)
									: t(
											'admin-residences:mailbox_test.waiting_code',
											{
												count: test.attemptsLeft
											}
										)}
							</dd>
						</>
					)}
				</dl>
			)}
		</DetailSection>
	)
}

const DELIVERY_LABEL = {
	sending: 'admin-residences:mailbox_test.delivery_states.sending',
	delivered: 'admin-residences:mailbox_test.delivery_states.delivered',
	refused: 'admin-residences:mailbox_test.delivery_states.refused'
} as const satisfies Record<MailboxTestStatus['delivery'], TKey>

const DELIVERY_TONE = {
	sending: 'info',
	delivered: 'success',
	refused: 'destructive'
} as const satisfies Record<keyof typeof DELIVERY_LABEL, BadgeVariant>

export { MailboxTestSection }
